import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { places } from '../data/places';
export function CuisinePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const restaurants = places.filter(p => p.categories.includes('Dining'));
  const cuisineTypes = [{
    title: 'Traditional Kenyan',
    description: 'Experience authentic Kenyan flavors with dishes like nyama choma, ugali, sukuma wiki, and githeri.',
    specialties: ['Nyama Choma', 'Ugali', 'Sukuma Wiki', 'Githeri']
  }, {
    title: 'International Cuisine',
    description: 'From Italian to Japanese, Lebanese to Indian, Nairobi offers a world of flavors.',
    specialties: ['Italian', 'Japanese', 'Lebanese', 'Indian']
  }, {
    title: 'Street Food',
    description: 'Discover the authentic taste of Nairobi through its vibrant street food scene.',
    specialties: ['Mutura', 'Smokie Pasua', 'Mandazi', 'Roasted Maize']
  }, {
    title: 'Fine Dining',
    description: 'Upscale restaurants offering contemporary cuisine in elegant settings.',
    specialties: ['Fusion', 'Contemporary', 'Gourmet', 'Wine Pairings']
  }];
  return <div className="min-h-screen bg-white" data-id="element-602">
      {/* Hero */}
      <div className="border-b border-gray-200" data-id="element-603">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-32" data-id="element-604">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-605">
            <h1 className="text-5xl md:text-7xl font-semibold text-charcoal mb-6 leading-tight" data-id="element-606">
              Local Cuisine
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-607">
              From street food to fine dining, discover Nairobi's diverse and
              delicious culinary scene.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-id="element-608">
        {/* Introduction */}
        <section className="mb-20" data-id="element-609">
          <div className="max-w-3xl" data-id="element-610">
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-611">
              Nairobi's culinary scene is as diverse as its people. The city
              offers everything from traditional Kenyan dishes to international
              cuisine, from street food stalls to upscale fine dining
              restaurants. Whether you're craving authentic nyama choma or
              contemporary fusion cuisine, Nairobi has something to satisfy
              every palate.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-612">
              The city's food culture reflects its cosmopolitan nature, with
              influences from across Africa, Asia, Europe, and the Middle East
              creating a unique gastronomic landscape.
            </p>
          </div>
        </section>

        {/* Cuisine Types */}
        <section className="mb-20" data-id="element-613">
          <h2 className="text-2xl font-semibold text-charcoal mb-8" data-id="element-614">
            Cuisine Types
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-615">
            {cuisineTypes.map((type, index) => <motion.div key={type.title} className="border border-gray-200 p-8" initial={{
            y: 20,
            opacity: 0
          }} whileInView={{
            y: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-616">
                <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-617">
                  {type.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-618">
                  {type.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-619">
                  {type.specialties.map(specialty => <span key={specialty} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs" data-id="element-620">
                      {specialty}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* Restaurants */}
        {restaurants.length > 0 && <section data-id="element-621">
            <h2 className="text-2xl font-semibold text-charcoal mb-8" data-id="element-622">
              Featured Restaurants
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-623">
              {restaurants.map((place, index) => <motion.div key={place.id} initial={{
            y: 20,
            opacity: 0
          }} whileInView={{
            y: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-624">
                  <Link to={`/place/${place.id}`} className="group block" data-id="element-625">
                    <div className="border border-gray-200 hover:border-charcoal transition-smooth overflow-hidden" data-id="element-626">
                      <div className="relative aspect-[4/3] overflow-hidden" data-id="element-627">
                        <img src={place.image} alt={place.name} className="w-full h-full object-cover group-hover:scale-105 transition-smooth" data-id="element-628" />
                      </div>
                      <div className="p-6" data-id="element-629">
                        <h3 className="text-xl font-semibold text-charcoal mb-2 group-hover:underline" data-id="element-630">
                          {place.name}
                        </h3>
                        <p className="text-gray-700 text-sm line-clamp-2 mb-4" data-id="element-631">
                          {place.description}
                        </p>
                        <div className="flex items-center gap-2 text-charcoal font-medium text-sm" data-id="element-632">
                          <span data-id="element-633">View details</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} data-id="element-634" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </section>}
      </div>

      <ScrollToTop data-id="element-635" />
      <Footer data-id="element-636" />
    </div>;
}